import { useEffect, useState } from "react";
import { CalendarDays, CheckCircle2, ExternalLink, IdCard, MapPin, ShieldCheck, User } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import {
  formatKycPendingReason,
  formatKycRejectionReason,
  isKycExpiredReason,
  kycPrepItems,
  kycRetryLabel,
  US_STATES,
} from "../lib/kyc";

export default function IdentityVerificationPrompt({ className = "", user, onUpdated }) {
  const [form, setForm] = useState({ fullName: "", dateOfBirth: "", state: "" });
  const [saving, setSaving] = useState(false);
  const status = user?.identity_verification_status || "not_submitted";
  const identityRequired = user?.identity_verification_required !== false;
  const approved = Boolean(user?.identity_verified) || status === "approved";
  const pending = status === "pending";
  const rejected = status === "rejected";
  const expired = rejected && isKycExpiredReason(user.identity_rejection_reason);
  const userId = user?.id || "";
  const savedFullName = user?.full_name || "";
  const savedDateOfBirth = user?.date_of_birth || "";
  const savedState = user?.state || "";

  useEffect(() => {
    if (!userId) return;
    setForm((current) => ({
      fullName: savedFullName || current.fullName || "",
      dateOfBirth: savedDateOfBirth || current.dateOfBirth || "",
      state: savedState || current.state || "",
    }));
  }, [userId, savedFullName, savedDateOfBirth, savedState]);

  useEffect(() => {
    if (!userId || !pending) return undefined;
    let active = true;
    let checking = false;
    const refreshStatus = async () => {
      if (checking || document.hidden) return;
      checking = true;
      try {
        const { data } = await api.post("/me/identity-verification/didit-refresh");
        if (active && data?.user) {
          await onUpdated?.();
        } else if (active && data?.status !== "pending") {
          await onUpdated?.();
        }
      } catch {
        // The webhook remains the primary update path; retry quietly.
      } finally {
        checking = false;
      }
    };
    refreshStatus();
    const timer = window.setInterval(refreshStatus, 5000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [userId, pending, onUpdated]);

  if (!user || approved || !identityRequired) return null;

  const submit = async (event) => {
    event.preventDefault();
    const fullName = form.fullName.trim().replace(/\s+/g, " ");
    if (fullName.length < 2) {
      toast.error("Enter your full legal name before ID verification.");
      return;
    }
    if (!form.dateOfBirth) {
      toast.error("Enter your date of birth before ID verification.");
      return;
    }
    if (!form.state) {
      toast.error("Choose your state before ID verification.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/me/identity-verification/didit-session", {
        full_name: fullName,
        date_of_birth: form.dateOfBirth,
        state: form.state,
      });
      await onUpdated?.();
      toast.success("Opening secure ID verification");
      window.location.assign(data.url);
    } catch (err) {
      toast.error(formatApiError(err));
      setSaving(false);
    }
  };

  const continueUrl = user.didit_session_url || "";

  return (
    <form onSubmit={submit} className={`rounded-2xl border-2 p-5 shadow-sm ${
      rejected ? "border-rose-200 bg-rose-50 text-slate-950" : "border-[#2F6FA5]/25 bg-white text-slate-950"
    } ${className}`}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#2F6FA5] text-white">
            <IdCard className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-lg leading-tight">Verify your ID</h3>
            <p className="mt-1 text-sm text-slate-600">
              {pending
                ? "Your Didit verification is in progress. Continue if you have not finished it yet."
                : rejected
                  ? expired
                    ? "Your last Didit session expired before it was finished. Start again when you are ready."
                    : "Your verification was not approved. Review the reason below and retry."
                  : "Secure ID verification is required only before wallet withdrawal. It usually takes 1-2 minutes."}
            </p>
            {!user.phone_verified && (
              <p className="mt-2 text-xs font-semibold text-amber-700">Phone verification is also required.</p>
            )}
          </div>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-black uppercase tracking-[0.12em] ${
          pending ? "bg-amber-100 text-amber-700" : rejected ? "bg-rose-100 text-rose-700" : "bg-sky-100 text-sky-700"
        }`}>
          {pending ? "pending" : rejected ? "rejected" : "required"}
        </span>
      </div>

      {rejected && (
        <div className="mt-4 rounded-xl border border-rose-200 bg-white p-4">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-rose-700">
            {expired ? "Session expired" : "Why verification was rejected"}
          </p>
          <p className="mt-2 text-sm font-semibold leading-6 text-rose-900">
            {formatKycRejectionReason(user.identity_rejection_reason)}
          </p>
          <p className="mt-2 text-xs text-slate-600">
            {expired
              ? "This is not a permanent rejection. Start KYC again and finish every Didit step before the timer ends."
              : "Fix the issue above, then submit verification again. You can resubmit from this same form."}
          </p>
        </div>
      )}

      {pending && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-amber-700">
            Why verification is pending
          </p>
          <p className="mt-2 text-sm font-semibold leading-6 text-amber-900">
            {formatKycPendingReason(user.identity_pending_reason)}
          </p>
          <p className="mt-2 text-xs text-slate-600">
            Complete the remaining Didit step, then tap continue or refresh the page.
          </p>
        </div>
      )}

      {!pending && (
        <>
          <KycPrepBox />
          <ProfileFields form={form} setForm={setForm} user={user} />
          <button
            type="submit"
            disabled={saving || form.fullName.trim().length < 2 || !form.dateOfBirth || !form.state}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2F6FA5] px-5 py-3 font-bold text-white hover:bg-[#4E91C8] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            <ShieldCheck className="h-4 w-4" />
            {saving ? "Opening..." : rejected ? kycRetryLabel(user.identity_rejection_reason) : "Start secure KYC"}
          </button>
        </>
      )}

      {pending && continueUrl && (
        <button
          type="button"
          onClick={() => window.location.assign(continueUrl)}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2F6FA5] px-5 py-3 font-bold text-white hover:bg-[#4E91C8] sm:w-auto"
        >
          <ExternalLink className="h-4 w-4" />
          Continue verification
        </button>
      )}
    </form>
  );
}

function KycPrepBox() {
  return (
    <div className="mt-4 rounded-xl border border-sky-100 bg-sky-50 p-4">
      <p className="text-xs font-black uppercase tracking-[0.14em] text-[#2F6FA5]">Before you start</p>
      <ul className="mt-3 space-y-2 text-sm font-semibold leading-5 text-slate-700">
        {kycPrepItems().map((item) => (
          <li key={item} className="flex gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#2F6FA5]" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProfileFields({ form, setForm, user }) {
  return (
    <>
      <label className="mt-5 block">
        <span className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
          <User className="h-3.5 w-3.5" /> Full legal name
        </span>
        <input
          value={form.fullName}
          onChange={(event) => setForm((current) => ({ ...current, fullName: event.target.value }))}
          minLength={2}
          maxLength={100}
          required
          placeholder="Name exactly as shown on ID"
          className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-950 outline-none transition focus:border-[#2F6FA5] focus:ring-2 focus:ring-[#2F6FA5]/20"
        />
      </label>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
            <CalendarDays className="h-3.5 w-3.5" /> Date of birth
          </span>
          <input
            type="date"
            value={form.dateOfBirth}
            onChange={(event) => setForm((current) => ({ ...current, dateOfBirth: event.target.value }))}
            max={todayInputValue()}
            readOnly={Boolean(user.date_of_birth)}
            disabled={Boolean(user.date_of_birth)}
            required
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-950 outline-none transition focus:border-[#2F6FA5] focus:ring-2 focus:ring-[#2F6FA5]/20 disabled:bg-slate-50 disabled:text-slate-500"
          />
        </label>
        <label className="block">
          <span className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
            <MapPin className="h-3.5 w-3.5" /> State
          </span>
          <select
            value={form.state}
            onChange={(event) => setForm((current) => ({ ...current, state: event.target.value }))}
            required
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-950 outline-none transition focus:border-[#2F6FA5] focus:ring-2 focus:ring-[#2F6FA5]/20"
          >
            <option value="">Select state</option>
            {US_STATES.map((item) => (
              <option key={item.code} value={item.code}>{item.name}</option>
            ))}
          </select>
        </label>
      </div>
    </>
  );
}

function todayInputValue() {
  return new Date().toISOString().slice(0, 10);
}
